import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { VscRefresh } from 'react-icons/vsc';
import { useDispatch, useSelector } from 'react-redux';
import AttenteRequete from '../../../../../hoc/AttenteRequete/AttenteRequete';
import { checkMot } from '../../../../../ressources/functionJS/checkMot';
import * as requestType from '../../../../../ressources/glossaires/glossaire_requetes';
import * as type_btn from '../../../../../ressources/glossaires/glossaire_type_de_bouton';
import * as s from '../../../../../ressources/StyleGlobal/StyleGlobal';
import * as actions from '../../../../../services/actions';
import Bouton from '../../../../UI/Bouton/Bouton';
import styles from './EditTableauxDeBordFavoris.module.scss';
import { insertAndShift } from 'services/utils/utils';

const COLUMN_CONFIG = [
  ['view_id', 'id', 110],
  ['project_id', 'id_projet', 130],
  ['view_name', 'nom', 180],
  ['view_title', 'view_title', 180],
];

const createColumn = (field, labelKey, width) => ({
  field,
  headerName: checkMot(labelKey),
  width,
  sortable: true,
});

const TableauxDeBord = () => {
  const dispatch = useDispatch();

  // Redux state
  const listeTableauxDeBord = useSelector(
    (state) =>
      state.vue.dashbords_list_by_user[state.authentifie.user_id]?.views_list ||
      []
  );
  const user_id = useSelector((state) => state.authentifie.user_id);
  const favorite_views_list = useSelector(
    (state) =>
      state.vue.dashbords_list_by_user[state.authentifie.user_id]
        ?.favorite_views_list || []
  );

  const [favorisSelectionne, setFavorisSelectionne] = useState([]);
  const [selected, setSelected] = useState([]);

  const columns = useMemo(
    () =>
      COLUMN_CONFIG.map(([field, labelKey, width]) =>
        createColumn(field, labelKey, width)
      ),
    []
  );

  // Initial data fetch
  useEffect(() => {
    if (!isNaN(user_id) && user_id !== '') {
      dispatch(actions.reqRecupererListeTableauxDeBord(user_id));
    }
  }, [user_id, dispatch]);

  // Update favorites when favorite_views_list changes
  useEffect(() => {
    if (favorite_views_list?.length > 0) {
      const updatedFavoris = listeTableauxDeBord
        .filter((tdB) =>
          favorite_views_list.map((value) => value[0]).includes(tdB.view_id)
        )
        .map((tdB) => {
          const favMatch = favorite_views_list.find(
            (fav) => fav[0] === tdB.view_id
          );
          return { ...tdB, indOrdre: favMatch?.[2] };
        })
        .sort((a, b) => a.indOrdre - b.indOrdre);

      setFavorisSelectionne(updatedFavoris);
      setSelected(favorite_views_list.map((value) => value[0]));
    }
  }, [favorite_views_list, listeTableauxDeBord]);

  const handleSelectionModelChange = useCallback(
    (newSelection) => {
      const newSelectedIds = Array.isArray(newSelection) ? newSelection : [];
      setSelected(newSelectedIds);

      const allRowsMap = new Map(
        listeTableauxDeBord.map((tdb) => [tdb.view_id, tdb])
      );

      setFavorisSelectionne((prev) => {
        const prevIds = prev.map((tdb) => tdb.view_id);
        const kept = prev.filter((tdb) => newSelectedIds.includes(tdb.view_id));
        const newOnes = newSelectedIds
          .filter((id) => !prevIds.includes(id))
          .map((id) => allRowsMap.get(id))
          .filter(Boolean);

        return [...kept, ...newOnes].map((tdb, ind) => ({
          ...tdb,
          indOrdre: ind,
        }));
      });
    },
    [listeTableauxDeBord]
  );

  const handleUpdateOrder = (fromIndex, toIndex) => {
    setFavorisSelectionne((prev) => {
      const newOrder = [...prev];
      return insertAndShift(newOrder, fromIndex, toIndex);
    });
  };

  const handleSaveFavorites = () => {
    if (favorisSelectionne) {
      const formattedFavorites = favorisSelectionne.map((tdb, ind) => [
        tdb.view_id,
        tdb.view_name,
        ind,
        tdb.project_id,
      ]);
      dispatch(
        actions.reqModifierListeTableauxDeBordFavorisUtilisateur(
          user_id,
          formattedFavorites
        )
      );
    }
  };

  // Render favorite list table rows
  const renderFavoritesList = () => {
    const header = (
      <thead>
        <tr>
          <th scope="col">Change</th>
          <th scope="col">{checkMot('view_name')}</th>
          <th scope="col">{checkMot('view_id')}</th>
        </tr>
      </thead>
    );

    const rows = favorisSelectionne.map((fav, index) => (
      <tr key={fav.view_id}>
        <th scope="row">
          <Bouton
            desactive={index === 0}
            cliquer={() => handleUpdateOrder(index - 1, index)}
          >
            ^
          </Bouton>
          <Bouton
            desactive={index === favorisSelectionne.length - 1}
            cliquer={() => handleUpdateOrder(index, index + 1)}
          >
            v
          </Bouton>
        </th>
        <td>{fav.view_name}</td>
        <td>{fav.view_id}</td>
      </tr>
    ));

    return [header, ...rows];
  };

  return (
    <div className={styles.Conteneur}>
      <div className={styles.conteneurTableauModificationOrdre}>
        <div className={styles.tableauModificationOrdre}>
          <table className={s.table}>{renderFavoritesList()}</table>
        </div>
        <div className={styles.conteneurBoutonModificationOrdre}>
          <AttenteRequete
            request_type={
              requestType.modifier_la_liste_des_tableaux_de_bord_favoris_utilisateur
            }
          >
            <Bouton cliquer={handleSaveFavorites}>
              {checkMot('enregistrer')}
            </Bouton>
          </AttenteRequete>
        </div>
      </div>
      <div className={styles.tableauSelection}>
        <AttenteRequete
          request_type={requestType.recuperation_de_la_liste_des_vues}
          str={checkMot('maj_vues_en_cour')}
        >
          <Bouton
            hoverText={checkMot('refresh')}
            type={type_btn.outil}
            rond={true}
            cliquer={() =>
              dispatch(actions.reqRecupererListeTableauxDeBord(user_id))
            }
          >
            <VscRefresh size={15} />
          </Bouton>
        </AttenteRequete>
        <DataGrid
          rows={listeTableauxDeBord}
          columns={columns}
          getRowId={(row) => row.view_id}
          rowSelectionModel={selected}
          onRowSelectionModelChange={handleSelectionModelChange}
          checkboxSelection
          disableMultipleRowSelection={false}
        />
      </div>
    </div>
  );
};

export default TableauxDeBord;
